import React, { useState, useEffect, useCallback } from 'react';
import {
  SlidersHorizontal, RefreshCw, X, Bookmark, ChevronRight,
  CheckCircle, WifiOff, Sparkles, MapPin, Banknote,
  Utensils, ChefHat, Coffee, Monitor, Keyboard,
  ShoppingBag, Store, BookOpen, GraduationCap, Wrench,
  HardHat, Zap, Scissors, Trophy, Dumbbell, Briefcase,
} from 'lucide-react';
import LetsCareersLogo from '../components/LetsCareersLogo';
import { fetchJobs } from '../services/api';
import { useApp } from '../context/AppContext';
import SwipeCard from '../components/SwipeCard';
import JobDetailSheet from '../components/JobDetailSheet';
import FilterDrawer from '../components/FilterDrawer';
import FilterSuggestion from '../components/FilterSuggestion';
import { useJobFilters } from '../hooks/useJobFilters';
import { JOB_TYPE_LABELS, labelOf, jobTitle } from '../constants/jobOptions';
import './ForYouPage.css';

const JOB_THEMES = {
  gastronomie:  { bg: '#6B2338', Icon: Utensils },
  restaurant:   { bg: '#6B2338', Icon: Utensils },
  küche:        { bg: '#6B2338', Icon: ChefHat },
  service:      { bg: '#6B2338', Icon: Utensils },
  café:         { bg: '#6B2338', Icon: Coffee },
  technologie:  { bg: '#0E1F3A', Icon: Monitor },
  it:           { bg: '#0E1F3A', Icon: Monitor },
  software:     { bg: '#0E1F3A', Icon: Keyboard },
  digital:      { bg: '#0E1F3A', Icon: Monitor },
  handel:       { bg: '#163324', Icon: ShoppingBag },
  einzelhandel: { bg: '#163324', Icon: ShoppingBag },
  supermarkt:   { bg: '#163324', Icon: Store },
  markt:        { bg: '#163324', Icon: Store },
  bildung:      { bg: '#122055', Icon: BookOpen },
  schule:       { bg: '#122055', Icon: GraduationCap },
  ausbildung:   { bg: '#122055', Icon: BookOpen },
  lehrer:       { bg: '#122055', Icon: GraduationCap },
  handwerk:     { bg: '#42200F', Icon: Wrench },
  bau:          { bg: '#42200F', Icon: HardHat },
  elektriker:   { bg: '#42200F', Icon: Zap },
  sanitär:      { bg: '#42200F', Icon: Wrench },
  tischler:     { bg: '#42200F', Icon: Scissors },
  sport:        { bg: '#1C1F2A', Icon: Trophy },
  fitness:      { bg: '#1C1F2A', Icon: Dumbbell },
  freizeit:     { bg: '#1C1F2A', Icon: Trophy },
  trainer:      { bg: '#1C1F2A', Icon: Dumbbell },
};
const DEFAULT_THEME = { bg: '#1A2840', Icon: Briefcase };

function getJobTheme(job) {
  const search = ((job.category || '') + ' ' + (job.title || '')).toLowerCase();
  for (const [key, theme] of Object.entries(JOB_THEMES)) {
    if (search.includes(key)) return theme;
  }
  return DEFAULT_THEME;
}

const normalizeJob = (job) => ({
  ...job,
  company: job.company || job.company_name,
  type: labelOf(JOB_TYPE_LABELS, job.job_type) || job.type,
  logo_url: job.logo_url || job.company_logo,
});

export default function ForYouPage() {
  const { user, openAuth, applyToJob, savedJobs, toggleSaveJob } = useApp();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [applyFeedback, setApplyFeedback] = useState(false);
  const [total, setTotal] = useState(null);
  const {
    filters, setFilters, defaults, params, activeCount, isMinor, noteIsMinor,
  } = useJobFilters();

  const topJob = queue[0];

  const loadJobs = useCallback(() => {
    setLoading(true);
    setError(false);
    setTotal(null);

    // wishes=1: Wünsche aus dem Profil steuern die Reihenfolge (nie die Treffermenge)
    fetchJobs({ limit: 30, ...params, wishes: 1 })
      .then(({ data }) => {
        const raw = Array.isArray(data)
          ? data
          : data?.jobs || data?.data || data?.results || data?.items || [];
        setQueue(raw.map(normalizeJob));
        setTotal(data?.pagination?.total ?? raw.length);
        // Der Endpoint meldet mit, ob der Nutzer minderjaehrig ist
        noteIsMinor(data?.is_minor);
      })
      .catch((err) => {
        console.error('fetchJobs error:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  }, [params, noteIsMinor]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  const handleSkip = useCallback(() => {
    setShowDetail(false);
    setQueue(prev => prev.slice(1));
  }, []);

  const handleSave = useCallback(() => {
    if (!user) { openAuth('register'); return; }
    if (topJob) toggleSaveJob(topJob);
  }, [user, openAuth, toggleSaveJob, topJob]);

  const handleApply = useCallback(async () => {
    if (!user) { openAuth('register'); return; }
    if (!topJob) return;
    try {
      await applyToJob(topJob);
      setShowDetail(false);
      setApplyFeedback(true);
      setTimeout(() => {
        setApplyFeedback(false);
        setQueue(prev => prev.slice(1));
      }, 3000);
    } catch {
      // API error – leave detail sheet open so user sees no false confirmation
    }
  }, [user, openAuth, applyToJob, topJob]);

  const isSaved = topJob
    ? savedJobs.some(j => (j?.id ?? j) === topJob.id)
    : false;
  const isEmpty = !loading && queue.length === 0;
  const theme = topJob ? getJobTheme(topJob) : DEFAULT_THEME;

  return (
    <div className="foryou-page">

      {/* ── Mobile layout (hidden on desktop via CSS) ── */}
      <div className="foryou-header">
        <div className="foryou-logo">
          <LetsCareersLogo variant="light" size="md" />
        </div>
        {!loading && queue.length > 0 && (
          <span className="foryou-count">{queue.length} Jobs in deiner Nähe</span>
        )}
        <button className="foryou-filter-btn" onClick={() => setShowFilter(true)}>
          <SlidersHorizontal size={20} />
          {activeCount > 0 && <span className="foryou-filter-dot" />}
        </button>
      </div>

      <FilterSuggestion filters={filters} defaults={defaults} total={total} onChange={setFilters} />

      <div className="foryou-stack">
        {loading && (
          <div className="foryou-skeleton">
            <div className="foryou-skeleton__logo skel skel--dark" />
            <div className="foryou-skeleton__content">
              <div className="foryou-skeleton__line skel skel--dark" style={{ width: '65%' }} />
              <div className="foryou-skeleton__line skel skel--dark" style={{ width: '48%' }} />
              <div className="foryou-skeleton__line foryou-skeleton__line--sm skel skel--dark" style={{ width: '32%' }} />
            </div>
            <div className="foryou-skeleton__actions">
              <div className="foryou-skeleton__btn skel skel--dark" />
              <div className="foryou-skeleton__btn skel skel--dark" />
            </div>
          </div>
        )}
        {error && (
          <div className="foryou-empty">
            <div className="foryou-empty__emoji"><WifiOff size={40} strokeWidth={1.4} /></div>
            <h3>Jobs konnten nicht geladen werden</h3>
            <p>Bitte überprüfe deine Internetverbindung.</p>
            <button className="foryou-empty__btn" onClick={loadJobs}>
              <RefreshCw size={16} /> Erneut versuchen
            </button>
          </div>
        )}
        {isEmpty && !error && (
          <div className="foryou-empty">
            <div className="foryou-empty__emoji"><Sparkles size={40} strokeWidth={1.4} /></div>
            <h3>Alle Jobs gesehen!</h3>
            <p>Du hast alle verfügbaren Jobs durchgeschaut.</p>
            <button className="foryou-empty__btn" onClick={loadJobs}>
              <RefreshCw size={16} /> Neu laden
            </button>
          </div>
        )}
        {!loading && topJob && <SwipeCard job={topJob} />}
        {applyFeedback && (
          <div className="foryou-apply-feedback">
            <CheckCircle size={52} strokeWidth={1.5} />
            <span>Bewerbung gesendet!</span>
          </div>
        )}
      </div>

      {!loading && topJob && (
        <div className="foryou-actions">
          <button
            className="foryou-action-btn foryou-action-btn--skip"
            onClick={handleSkip}
            aria-label="Überspringen"
          >
            <X size={18} />
            <span>Skippen</span>
          </button>
          <button
            className={`foryou-action-btn foryou-action-btn--save${isSaved ? ' saved' : ''}`}
            onClick={handleSave}
            aria-label={isSaved ? 'Gespeichert' : 'Merken'}
          >
            <Bookmark size={20} fill={isSaved ? 'currentColor' : 'none'} />
          </button>
          <button
            className="foryou-action-btn foryou-action-btn--detail"
            onClick={() => setShowDetail(true)}
            aria-label="Mehr Infos"
          >
            <span>Mehr Infos</span>
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* ── Desktop layout (hidden on mobile via CSS) ── */}
      <div className="foryou-desktop">
        {loading && (
          <div className="foryou-desktop__state">
            <div className="foryou-loading__spinner" />
            <p>Jobs werden geladen...</p>
          </div>
        )}
        {error && !loading && (
          <div className="foryou-desktop__state">
            <WifiOff size={40} strokeWidth={1.4} />
            <h3>Jobs konnten nicht geladen werden</h3>
            <p>Bitte überprüfe deine Internetverbindung.</p>
            <button className="foryou-empty__btn" onClick={loadJobs}>
              <RefreshCw size={16} /> Erneut versuchen
            </button>
          </div>
        )}
        {isEmpty && !error && (
          <div className="foryou-desktop__state">
            <Sparkles size={40} strokeWidth={1.4} />
            <h3>Alle Jobs gesehen!</h3>
            <p>Du hast alle verfügbaren Jobs durchgeschaut.</p>
            <button className="foryou-empty__btn" onClick={loadJobs}>
              <RefreshCw size={16} /> Neu laden
            </button>
          </div>
        )}

        {!loading && topJob && (
          <div className="foryou-desktop__main">
            {/* Left: full-height visual panel — no left padding, touches sidebar */}
            <div className="foryou-desktop__visual" style={{ background: theme.bg }}>
              <theme.Icon size={88} strokeWidth={1.1} color="rgba(255,255,255,0.88)" />
              <div className="foryou-desktop__visual-info">
                <span className="foryou-desktop__company-name">{topJob.company}</span>
                {topJob.type && (
                  <span className="foryou-desktop__type-badge">{topJob.type}</span>
                )}
                {topJob.matches_wishes && (
                  <span className="foryou-desktop__type-badge foryou-match-badge">
                    <Sparkles size={13} /> Passt zu dir
                  </span>
                )}
              </div>
            </div>

            {/* Right: topbar + job details + actions */}
            <div className="foryou-desktop__detail">
              <div className="foryou-desktop__topbar">
                <span className="foryou-desktop__topbar-count">
                  {queue.length > 0 ? `${queue.length} Jobs in deiner Nähe` : ''}
                </span>
                <button className="foryou-filter-btn foryou-filter-btn--light" onClick={() => setShowFilter(true)}>
                  <SlidersHorizontal size={18} />
                  {activeCount > 0 && <span className="foryou-filter-dot" />}
                </button>
              </div>

              <div className="foryou-desktop__info">
                <h2 className="foryou-desktop__title">{jobTitle(topJob.title)}</h2>
                <div className="foryou-desktop__meta">
                  {topJob.location && (
                    <span className="foryou-desktop__meta-item">
                      <MapPin size={15} /> {topJob.location}
                    </span>
                  )}
                  {topJob.salary && (
                    <span className="foryou-desktop__meta-item">
                      <Banknote size={15} /> {topJob.salary}
                    </span>
                  )}
                </div>
                {topJob.description && (
                  <p className="foryou-desktop__desc">{topJob.description}</p>
                )}
              </div>

              <div className="foryou-desktop__actions">
                <div className="foryou-desktop__primary-row">
                  <button className="foryou-desktop__skip-btn" onClick={handleSkip}>
                    Überspringen <ChevronRight size={15} />
                  </button>
                  <button className="foryou-desktop__skip-btn" onClick={() => setShowDetail(true)}>
                    Mehr Infos
                  </button>
                  <button
                    className={`foryou-desktop__bookmark${isSaved ? ' saved' : ''}`}
                    onClick={handleSave}
                    aria-label={isSaved ? 'Gespeichert' : 'Merken'}
                  >
                    <Bookmark size={18} fill={isSaved ? 'currentColor' : 'none'} />
                  </button>
                  <button className="foryou-desktop__apply" onClick={handleApply}>
                    Jetzt bewerben
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {applyFeedback && (
          <div className="foryou-desktop__feedback">
            <CheckCircle size={56} strokeWidth={1.4} />
            <span>Bewerbung gesendet!</span>
          </div>
        )}
      </div>

      {/* Overlays: always rendered regardless of breakpoint */}
      {showDetail && topJob && (
        <JobDetailSheet
          job={topJob}
          onClose={() => setShowDetail(false)}
          onApply={handleApply}
        />
      )}
      {showFilter && (
        <FilterDrawer
          onClose={() => setShowFilter(false)}
          onApply={setFilters}
          values={filters}
          defaults={defaults}
          isMinor={isMinor}
        />
      )}
    </div>
  );
}
